import { appFormsTreeFieldOptionsMap } from 'app/services/autoLockForms/autoLockForms';
import _ from 'lodash';

import {
  OPEN_LOAN_SECTION_AREA_LABEL,
  ToolConfirmationPolicy,
  ToolConfirmationStatus,
} from 'constants/aiChat';
import { jsonParse, jsonStringify } from 'utils/json';

import {
  TOOL_CONFIRMATION_FLOW,
  TOOL_CONFIRMATION_LABELS,
  TOOL_RESULT_SUMMARY_LIMIT,
} from './ToolConfirmationCard.constants';

export const formatToolArguments = toolArguments => {
  if (_.isNil(toolArguments)) {
    return '';
  }

  if (_.isString(toolArguments)) {
    return toolArguments;
  }

  try {
    return jsonStringify(toolArguments, null, 2);
  } catch {
    return String(toolArguments);
  }
};

export const formatToolResult = result => {
  if (_.isNil(result)) {
    return '';
  }

  if (_.isString(result)) {
    return result;
  }

  try {
    return jsonStringify(result);
  } catch {
    return String(result);
  }
};

export const getResultSummary = formattedResult => {
  if (formattedResult.length <= TOOL_RESULT_SUMMARY_LIMIT) {
    return { summary: formattedResult, hasMore: false };
  }

  return {
    summary: `${formattedResult.slice(0, TOOL_RESULT_SUMMARY_LIMIT)}…`,
    hasMore: true,
  };
};

export const getCancelledMessage = reason => {
  if (_.isNil(reason) || reason === '') {
    return TOOL_CONFIRMATION_LABELS.CANCELLED_DEFAULT;
  }

  return `${TOOL_CONFIRMATION_LABELS.CANCELLED_WITH_REASON_PREFIX}${reason}`;
};

export const mapPendingToolExecutionToConfirmation = pendingToolExecution => {
  if (_.isNil(pendingToolExecution)) {
    return null;
  }

  const { toolCallId, toolName, friendlyName, parameters } = pendingToolExecution;

  return {
    toolCallId,
    toolName: toolName ?? null,
    friendlyName: _.isEmpty(friendlyName) ? null : friendlyName,
    toolArguments: parameters ?? null,
    policy: ToolConfirmationPolicy.CONFIRM,
    status: ToolConfirmationStatus.PENDING,
    flow: TOOL_CONFIRMATION_FLOW.FRONTEND,
  };
};

const parseToolArgumentsObject = toolArguments => {
  if (_.isNil(toolArguments)) {
    return null;
  }

  if (_.isPlainObject(toolArguments)) {
    return toolArguments;
  }

  if (_.isString(toolArguments)) {
    try {
      const parsed = jsonParse(toolArguments);

      return _.isPlainObject(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  return null;
};

const titleCaseToolName = toolName => {
  if (_.isEmpty(toolName)) {
    return null;
  }

  return toolName
    .split('_')
    .filter(part => part.length > 0)
    .map(part => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
};

const resolveFieldLabel = fieldId => {
  if (_.isEmpty(fieldId)) {
    return null;
  }

  const fieldOption = appFormsTreeFieldOptionsMap[fieldId];

  if (_.isNil(fieldOption)) {
    return null;
  }

  return fieldOption.name ?? fieldOption.label ?? null;
};

const buildOpenLoanSectionSummary = parsedArgs => {
  const fieldId = parsedArgs?.fieldId;
  const fieldLabel = resolveFieldLabel(fieldId);

  if (!_.isEmpty(fieldLabel)) {
    return {
      headline: TOOL_CONFIRMATION_LABELS.HIGHLIGHT_FIELD_HEADLINE,
      detail: `${TOOL_CONFIRMATION_LABELS.HIGHLIGHT_FIELD_DETAIL_PREFIX}${fieldLabel}`,
    };
  }

  const headline = TOOL_CONFIRMATION_LABELS.OPEN_LOAN_SECTION_HEADLINE;
  const area = parsedArgs?.area;
  const areaLabel = area ? OPEN_LOAN_SECTION_AREA_LABEL[area] : null;

  if (_.isEmpty(areaLabel)) {
    return { headline, detail: null };
  }

  return {
    headline,
    detail: `${TOOL_CONFIRMATION_LABELS.OPEN_LOAN_SECTION_DETAIL_PREFIX}${areaLabel}`,
  };
};

const buildEditFieldSummary = (parsedArgs, fallback) => {
  const fieldId = parsedArgs?.fieldId ?? parsedArgs?.field;

  if (_.isEmpty(fieldId)) {
    return fallback;
  }

  const fieldLabel = resolveFieldLabel(fieldId) ?? fieldId;

  return {
    headline: TOOL_CONFIRMATION_LABELS.EDIT_FIELD_HEADLINE,
    detail: `${TOOL_CONFIRMATION_LABELS.EDIT_FIELD_DETAIL_PREFIX}${fieldLabel}`,
  };
};

const SUMMARY_RESOLVERS = {
  open_loan_section: buildOpenLoanSectionSummary,
  edit_field: buildEditFieldSummary,
};

export const buildToolConfirmationSummary = ({ toolName, friendlyName, toolArguments }) => {
  const fallbackHeadline = _.isEmpty(friendlyName)
    ? (titleCaseToolName(toolName) ?? TOOL_CONFIRMATION_LABELS.UNKNOWN_TOOL_HEADLINE)
    : friendlyName;
  const fallback = { headline: fallbackHeadline, detail: null };
  const resolver = SUMMARY_RESOLVERS[toolName];

  if (_.isNil(resolver)) {
    return fallback;
  }

  return resolver(parseToolArgumentsObject(toolArguments), fallback);
};
